/**
 * Провайдеры Refine.
 *
 * Форма — как у соседа (`/claude/claude/supply/web/src/lib/providers.ts`), и
 * политика та же: «авторизован» значит «сервер подтвердил сессию», а не
 * «в localStorage что-то лежит». Сессия живёт в httpOnly-cookie, JS её не
 * видит вовсе, поэтому `check` спрашивает `/api/me` — узнать, жива ли сессия,
 * можно только у того, кто её выдал.
 */

import type { AuthProvider, DataProvider } from "@refinedev/core";

import { api, ApiError } from "@/lib/api";

/** Кто вошёл. Приходит из CRM (`sUser`), своей таблицы пользователей нет. */
export type CurrentUser = { id: number; name: string; short: string };

/** Ответы `/api/me` и `/api/login` — пользователь завёрнут в поле `user`. */
type UserReply = { user: CurrentUser };

export const authProvider: AuthProvider = {
  check: async () => {
    try {
      await api.get<UserReply>("/api/me");
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: "/login", logout: true };
    }
  },

  // Одно поле — пароль: он же идентифицирует человека в CRM. Логина в наших
  // системах не существует, поэтому и в провайдере его нет.
  login: async ({ password }: { password: string }) => {
    try {
      await api.post<UserReply>("/api/login", { password });
      return { success: true, redirectTo: "/placement" };
    } catch (error) {
      // Разные причины — разные слова. «Неверный пароль» на упавшей базе
      // заставил бы сотрудника подбирать пароль, которого он не забывал.
      const status = error instanceof ApiError ? error.status : 0;
      const message =
        status === 401
          ? "Неверный пароль"
          : status === 429
            ? "Слишком много попыток, подождите минуту"
            : "Сервис недоступен";
      return {
        success: false,
        error: { name: `login_${status || "failed"}`, message },
      };
    }
  },

  logout: async () => {
    // Сессию гасит сервер: без этого строка в базе осталась бы открытой, и
    // украденная cookie продолжала бы работать. Отказ проглатывается
    // намеренно — человек нажал «Выйти» и обязан оказаться на экране входа
    // независимо от того, доехал ли запрос.
    try {
      await api.post<{ ok: boolean }>("/api/logout", {});
    } catch {
      /* см. выше */
    }
    return { success: true, redirectTo: "/login" };
  },

  getIdentity: async () => {
    try {
      const reply = await api.get<UserReply>("/api/me");
      return reply.user;
    } catch {
      return null;
    }
  },

  onError: async (error) => {
    // 401 приходит и от истёкшей сессии, и от погашенной администратором.
    // В обоих случаях ответ один: на форму входа, без попытки «починить».
    if (error instanceof ApiError && error.status === 401) {
      return { logout: true, redirectTo: "/login", error };
    }
    return { error };
  },
};

/**
 * Строка отчёта: КАРТОЧКА ТОВАРА внутри короба, а не короб. В одном коробе
 * бывает несколько товаров, поэтому строк всегда не меньше, чем коробов, —
 * и число коробов считается отдельно (`uniqueBoxCount`).
 */
export type Row = {
  rank: number;
  box_barcode: string;
  korob_barcode: string;
  sort_order: number;
  card_id: number;
  model_id: number;
  image_url: string;
  product: string;
  product_barcode: string;
  size: string;
  picks: number;
  picks_per_day: number;
  abc: string;
  qty_in_box: number;
  qty_total: number;
  reserve_boxes: number[];
  coverage_pct: number;
};

export type Report = {
  capacity: number;
  days: number;
  date_from: string;
  date_to: string;
  total_boxes: number;
  total_picks: number;
  picks_per_day: number;
  picks_wb: number;
  picks_ozon: number;
  /** Спрос Ozon приезжает не всегда и не с любой даты — экран обязан сказать. */
  ozon: { available: boolean; since: string };
  threshold_per_day: number;
  cards_below_threshold: number;
  boxes_free: number;
  coverage_pct: number;
  rows: Row[];
};

export type Location = { location_id: number; name: string; units: number };
export type Locations = {
  default_location_id: number;
  locations: Location[];
};

/**
 * Откуда товар: поставщик и категория. Приезжают в каждой строке и в карточке.
 *
 * Нули и пустые строки означают «в каталоге не проставлено», а не «поле не
 * пришло»: у 8 карточек CRM номер модели пуст вовсе, и поля тогда всё равно
 * есть — экран разбирает один вид ответа, а не два.
 */
export type Supplier = { id: number; name: string; code: string };
export type Category = { id: number; name: string };

/** Строка списка товаров. Товар — это МОДЕЛЬ, а не отдельный размер. */
export type ProductRow = {
  model_id: number;
  name: string;
  image_url: string;
  article: string;
  /** Число БАРКОДОВ. На один размер их бывает несколько — см. `sizes`. */
  cards: number;
  /** Размерный ряд, каждый размер один раз. */
  sizes: string[];
  supplier: Supplier;
  category: Category;
  /**
   * Штук в коробах — ОДНО число. Короба считаются единым целым: и упакованные,
   * и те, что ещё набирают. Не в счёт только отключённые короба и уехавшие в
   * поставку — их отсекает канон короба на стороне сервиса.
   */
  units: number;
  /** В скольких коробах лежит этот товар. */
  boxes: number;
  /**
   * В каких городах лежат эти штуки, по убыванию остатка. Пусто — товар
   * заведён на маркетплейсе, но сейчас его нет ни в одном коробе.
   *
   * Имя города здесь не едет: оно одно на весь ответ и стоит в `cities`
   * сводки. Повторять его в каждой из 1 771 строки значило бы возить по
   * складской сети одно и то же слово тысячу семьсот раз.
   */
  by_city: { city_id: number; units: number }[];
};

/** Город сводки: сколько штук лежит в его коробах. */
export type ProductCity = { city_id: number; name: string; units: number };

export type Products = {
  filter: {
    model_id: number | null;
    barcode: string;
    supplier_id: number | null;
    category_id: number | null;
    city_id: number | null;
    /** Какая карточка совпала с набранным баркодом. 0 — фильтра не было. */
    matched_card_id: number;
  };
  total: number;
  shown: number;
  limit: number;
  /**
   * Сколько из найденных товаров ДЕЙСТВИТЕЛЬНО лежат на складе. Список — это
   * каталог маркетплейса, и `total` больше не значит «столько у нас есть»:
   * 683 из 1 771 (замер 07.09.26).
   */
  in_stock: number;
  /** Штук в коробах по ВСЕМУ, что нашлось, а не по показанной странице. */
  units: number;
  /** Те же штуки по городам. Сумма равна `units` — считаны одним проходом. */
  cities: ProductCity[];
  products: ProductRow[];
};

/**
 * Справочники отбора. `models` рядом с именем — обещание: столько строк и
 * покажет список, если выбрать эту строку.
 *
 * Обещание у пород РАЗНОЕ, потому что и фильтры разной природы. Поставщик и
 * категория считаны по каталогу маркетплейса — по тому же множеству, из
 * которого состоит список. Город — по коробам: «Воронеж · 283» это товары,
 * которые СЕЙЧАС там лежат, а не заведённые на маркетплейсе.
 */
export type FilterOption = {
  id: number;
  name: string;
  /** Короткий код поставщика из CRM. У категорий и городов пуст. */
  code: string;
  models: number;
};

export type ProductFilters = {
  suppliers: FilterOption[];
  categories: FilterOption[];
  cities: FilterOption[];
};

/** Один размер одной модели: собственный баркод и собственный остаток. */
export type ProductCard = {
  card_id: number;
  barcode: string;
  size: string;
  color: string;
  /** 0 — товар на Ozon не заведён. */
  ozon_sku: number;
  image_url: string;
  /** Штук ЭТОГО размера в коробах. */
  units: number;
  /**
   * Штук ЭТОГО размера в салонах — вне коробов. Своим числом, потому что это
   * ДРУГОЕ место хранения: короб и салон вместе дают весь остаток размера, но
   * идти за вещью в них приходится по-разному.
   */
  units_salon: number;
};

/** Группа мест внутри салона: «МП / Короб», «РСТ / 854», «Транзит Приехал». */
export type SalonGroup = {
  group_id: number;
  name: string;
  units: number;
  /** Мест в группе: одно место с двумя размерами — это одно место. */
  places: number;
  by_card: { card_id: number; units: number }[];
};

/** Салон с товаром: сколько штук и по каким группам мест они разложены. */
export type ProductSalon = {
  city_id: number;
  name: string;
  units: number;
  groups: SalonGroup[];
};

/** Короб, в котором лежит товар, вместе со всем своим содержимым. */
export type ProductBox = {
  place_id: number;
  /** Номер короба — то, чем его зовут на складе. */
  code: string;
  korob_barcode: string;
  /** Код места: им короб СКАНИРУЮТ, когда кладут и достают. */
  place_barcode: string;
  city: string;
  units_this: number;
  units_total: number;
  by_card: { card_id: number; units: number }[];
};

export type Product = {
  model_id: number;
  name: string;
  image_url: string;
  article: string;
  model_name: string;
  supply_code: string;
  supplier: Supplier;
  category: Category;
  cards: ProductCard[];
  /** Штук в коробах — весь остаток модели там, одним числом. */
  units: number;
  boxes: ProductBox[];
  /** Весь остаток вне коробов: вторая половина того же склада. */
  units_salon: number;
  salons: ProductSalon[];
};

/**
 * Конец переезда: место, его группа и город. Общий тип для ленты движений и
 * для карточки серии — обе половины приходят из одного `crm/move.py::Point`,
 * и два похожих типа разошлись бы на первой правке одного из них.
 *
 * Всё, кроме номера, может быть `null`: место могли удалить или переименовать,
 * а у первой записи истории вещи предыдущего места нет вовсе.
 */
export type MovePoint = {
  place_id: number | null;
  name: string | null;
  group: string | null;
  city: string | null;
  /**
   * Номер места (`sPlace.sortOrder`) и признак короба — ими место ЗОВУТ.
   *
   * Короб зовётся номером, прочие места — именем: общее правило
   * `lib/places.ts::placeLabel`, то же, каким зовут места списки раздела
   * «Баркод». `korob` здесь — ГРУППА 218, половина канона: вопрос ленты «это
   * короб или зал», и уехавший в поставку короб обязан остаться коробом в
   * рассказе о том, где вещь была.
   */
  code: string;
  korob: boolean;
};

/**
 * Строка ленты движений: один переезд одной вещи.
 *
 * Одна форма на две ленты — серии (`GET /api/move/history`) и баркода
 * (`GET /api/barcode/{код}/history`). В ленте серии `serial` у всех строк
 * один, в ленте баркода — разный, и без него она читалась бы как один
 * непрерывный маршрут, которым не является.
 */
export type MoveRow = {
  move_id: number;
  serial: number;
  at: string;
  type_id: number;
  type: string | null;
  user: string | null;
  from: MovePoint;
  to: MovePoint;
  /**
   * Складская задача FBS, сделавшая этот переезд, или `null` — переезд ручной.
   *
   * `null` — это ОТВЕТ, а не пробел: вещь двигал человек (выемка из короба,
   * возврат, инвентаризация). «Спросить было нечем» говорит другое поле —
   * `tasks_known` в ответе целиком.
   */
  task: MoveTask | null;
  /**
   * ТОВАР той вещи, что ехала (задача владельца 10.09.26).
   *
   * В `sMove` про товар нет ничего — там только номер серии, — и строка ленты
   * места называла уехавшее платье одним числом. Читается по текущей привязке
   * `sSerial.mpProductCardID`, форма общая с карточками серии и задачи.
   *
   * `null` — вещь на маркетплейс не заводили; тогда номер серии и остаётся
   * всем, чем её можно назвать.
   */
  card: SerialCard | null;
  /**
   * ПРИЧИНА, если этим перемещением вещь списали или вернули в оборот
   * (задача владельца 11.09.26).
   *
   * Тип перемещения говорит, ЧТО сделали («Списано было»), но не говорит
   * зачем: «fbswb» значит «уехала в поставку ВБ», «Утеря» — совсем другой
   * разговор к тому же складу. Связь — `sSerial_spisanie.moveID`, и она
   * однозначна: на всей таблице номер перемещения не повторяется ни разу.
   *
   * Форма ТА ЖЕ, что у списания в карточке серии (`SerialSpisanie`): одна и
   * та же строка учёта обязана читаться одинаково с обоих экранов.
   *
   * `null` — вещь просто переложили, и это ответ, а не пробел.
   */
  spisanie: SerialSpisanie | null;
};

/**
 * Задание ПЛОЩАДКИ за складской задачей: что с этой вещью у ВБ или Ozon.
 *
 * Статуса два и они про разное: `status` — состояние самого задания
 * («В доставке»), `substatus` — взгляд площадки на заказ («Покупатель
 * отказался в пункте выдачи»). Имена статусов есть только у ВБ: в зеркале
 * Ozon лежат одни коды, и своего перевода у нас нет намеренно.
 */
export type MoveTaskMp = {
  /** `wb` | `ozon` — та же буква, что в адресе раздела «FBS Короба». */
  platform: string;
  /** Короткое имя площадки для тесной строки: «ВБ», «Озон». */
  platform_name: string;
  status: string | null;
  status_name: string | null;
  substatus: string | null;
  substatus_name: string | null;
};

/**
 * Привязка переезда к складской задаче FBS.
 *
 * Номер здесь НАШ (`fbs.fbs_task.id`), а не номер задания ВБ или отправления
 * Ozon: складская работа общая на обе площадки, и адрес у неё один. Внешние
 * НОМЕРА показывает карточка задачи; внешний СТАТУС приезжает в `mp` и стоит
 * в строке рядом со статусом склада — порознь первый читается как весь ответ.
 *
 * `mp: null` — либо задания у задачи нет вовсе, либо зеркало площадки молчит.
 */
export type MoveTask = {
  id: number;
  /** `pick` — подбор с полки, `pack` — укладка в короб отгрузки. */
  role: "pick" | "pack";
  role_name: string;
  status: string;
  status_name: string | null;
  platform_id: number;
  platform: string | null;
  serial: number | null;
  mp: MoveTaskMp | null;
};

export type MoveHistory = {
  serial: number;
  count: number;
  /** Есть ли строки за показанными. По нему живёт кнопка «ещё». */
  has_more: boolean;
  /**
   * Спрашивали ли базу задач вообще. `false` — не смогли (песочная полоса,
   * отказ базы), и тогда пустой `task` у строк ничего не значит.
   */
  tasks_known: boolean;
  moves: MoveRow[];
};

/** Лента движений всех вещей одного баркода. */
export type BarcodeHistory = {
  barcode: string;
  card_id: number;
  /** Место, которым лента сужена, или `null` — вся история размера. */
  place_id: number | null;
  count: number;
  has_more: boolean;
  tasks_known: boolean;
  moves: MoveRow[];
};

/**
 * Половина складской задачи: подбор или упаковка.
 *
 * Обе устроены одинаково — «кто, каким перемещением, когда взял и когда
 * закончил», — и это одна форма, заданная дважды: разные поля у них означали
 * бы, что на экране их надо читать по-разному.
 */
export type FbsTaskStep = {
  role: "pick" | "pack";
  role_name: string;
  user_id: number | null;
  user: string | null;
  /**
   * Место ПО УЧЁТУ СБОРКИ: откуда снимали при подборе, куда клали при
   * упаковке. Читается из `fbs` и живёт без CRM — этим и отличается от `move`.
   */
  place: MovePoint | null;
  /** Перемещение CRM. Тот же номер стоит в строке ленты движений. */
  move_id: number | null;
  /**
   * САМО перемещение — оба конца и серия, которая по нему ехала.
   *
   * Форма та же, что у строки ленты (`MoveRow` без привязки к задаче, без
   * товара и без причины списания): товар у карточки задачи свой, вверху, а
   * списания у подбора и упаковки не бывает вовсе — их перемещения делает
   * сборка, а не списание. Одно и то же перемещение при этом обязано
   * читаться одинаково с обоих экранов. `null` — CRM молчала или запись
   * перемещения удалили; тогда остаётся `place`.
   */
  move: Omit<MoveRow, "task" | "card" | "spisanie"> | null;
  claimed_at: string | null;
  done_at: string | null;
};

/** Наша половина задачи: то, что о ней знает склад. */
export type FbsTaskBody = {
  id: number;
  status: string;
  status_name: string | null;
  platform_id: number;
  platform: string | null;
  serial: number | null;
  card_id: number | null;
  location_id: number | null;
  znak_id: number | null;
  /**
   * Серия, на которой лежит ЭТОТ ЗНАК по учёту CRM (`sBarcodeZnak.serial`).
   *
   * С `serial` задачи совпадает НЕ всегда, и это ожидаемо: наклейку клеит
   * человек, автоподбора знака по серии в сборке нет намеренно. Замер
   * 09.09.26 — из 1 273 задач со знаком серии расходятся у 708, и во всех 708
   * случаях товар тот же самый; знака от ЧУЖОГО товара нет ни одного.
   */
  znak_serial: number | null;
  znak_sent_at: string | null;
  created_at: string | null;
  cancelled_at: string | null;
  pick: FbsTaskStep;
  pack: FbsTaskStep;
};

/** Город задачи — тот же вид и в карточке, и в строке списка. */
export type FbsTaskCity = {
  id: number;
  name: string | null;
  short: string | null;
};

/**
 * Строка СПИСКА задач: сама задача плюс её товар и город.
 *
 * Форма задачи та же, что в карточке (`FbsTaskBody`): список ведёт в карточку,
 * и задача, названная по пути двумя наборами полей, читалась бы как две разные.
 */
export type FbsTaskRow = FbsTaskBody & {
  card: SerialCard | null;
  city: FbsTaskCity | null;
};

/** Переключатель списка: значение, имя и сколько за ним задач. */
export type FbsTaskFacet = {
  id?: number;
  code?: string;
  name: string | null;
  tasks: number;
};

/**
 * Список складских задач FBS: что склад собирает и где.
 *
 * Числа переключателей считаются по отбору БЕЗ своего поля — «сколько будет,
 * если переключить», а не «сколько в выбранном». Иначе выбранный город
 * показывал бы своё число, а остальные нули.
 */
export type FbsTasks = {
  filter: {
    city_id: number;
    platform: string;
    status: string;
    serial: number;
    task: number;
    limit: number;
  };
  total: number;
  shown: number;
  limit: number;
  cities: FbsTaskFacet[];
  platforms: FbsTaskFacet[];
  statuses: FbsTaskFacet[];
  rows: FbsTaskRow[];
  /** Читалась ли CRM. `false` — в строках номера вместо имён, товаров и городов. */
  sources: { crm: boolean };
};

/**
 * Задание Wildberries за складской задачей.
 *
 * Статуса ДВА, и путать их нельзя: `status` — наш взгляд на задание
 * (supplierStatus), `substatus` — взгляд ВБ на заказ (wbStatus). Отказы
 * покупателей живут во втором, и судить по нему «уехала ли вещь» нельзя.
 */
export type FbsTaskWb = {
  row_id: number;
  order_id: number | null;
  rid: string | null;
  nm_id: number | null;
  vendor_code: string | null;
  barcode: string | null;
  status: string | null;
  status_name: string | null;
  substatus: string | null;
  substatus_name: string | null;
  ordered_at: string | null;
  cancel_seen_at: string | null;
  spisanie_id: number | null;
  spisanie_at: string | null;
  supply: {
    id: string | null;
    status: string | null;
    status_name: string | null;
    closed_at: string | null;
    scanned_at: string | null;
  };
};

/**
 * Отправление Ozon за складской задачей и акт, которым оно уехало.
 *
 * `carriage_id: null` — отправление ни в один акт не попало, то есть вещь
 * физически ещё у нас.
 */
export type FbsTaskOzon = {
  posting_id: number;
  posting_number: string;
  order_number: string | null;
  status: string | null;
  substatus: string | null;
  in_process_at: string | null;
  shipment_date: string | null;
  carriage_id: number | null;
  carriage_status: string | null;
  departure_date: string | null;
  act_status: string | null;
  /** Разбор отмены: успели ли сдать посылку. `null` — отправление не отменено. */
  cancel: FbsCancel | null;
};

/**
 * Карточка складской задачи целиком: наша половина плюс задание маркетплейса.
 *
 * Флагов ТРИ, потому что и источников три — задача в `fbs`, задание в зеркале
 * кабинета, имена в CRM, — и гаснут они порознь. Пустое поле без флага
 * читалось бы как «этого нет», а не «не смогли прочитать».
 */
export type FbsTaskCard = {
  task: FbsTaskBody;
  location: FbsTaskCity | null;
  /** Тот же товар, что в карточке серии: форму собирает одна `card_json`. */
  card: SerialCard | null;
  wb: FbsTaskWb | null;
  ozon: FbsTaskOzon | null;
  sources: { crm: boolean; wb: boolean; ozon: boolean };
};

/**
 * Место в разрезе одного баркода: что там лежит и что там было.
 *
 * `units` — сколько годных вещей этого размера лежит здесь СЕЙЧАС. Ноль
 * значит «уже не лежит», и у таких мест смотрят `last_out` — когда отсюда
 * забрали в последний раз.
 *
 * `korob` — место из коробов маркетплейса (группа 218). Это половина канона
 * короба намеренно: короб, уехавший в поставку, остаётся коробом в ленте «где
 * вещь была», хотя под отгрузку он уже недоступен.
 */
/**
 * Поставка, за которой числится место, — то есть куда уехал короб.
 *
 * Бывает только у коробов маркетплейса и только после того, как короб отдан:
 * `sPlace.mpSupplyID` пуст, пока его набирают. Для строки списка это ответ на
 * «куда ушёл товар отсюда» (задача владельца 11.09.26).
 *
 * `date` и `date_fact` — разные вопросы: «на когда поставка» и «когда её
 * отгрузили». У Ozon второго нет вовсе. `warehouse` — склад СДАЧИ, `region` —
 * куда товар поедет дальше (кластер Ozon, макрорегион ВБ); при кроссдоке они
 * не совпадают.
 */
export type PlaceSupply = {
  id: number;
  /** `wb` | `ozon` — та же буква, что в адресе раздела «FBS Короба». */
  platform: string | null;
  /** Короткое имя площадки для тесной строки: «ВБ», «Озон». */
  platform_name: string | null;
  /** Как поставку зовут в кабинете: `WB-GI-273619491` или номер заявки Ozon. */
  code: string | null;
  /** «Номер Поставки» ВБ — им её ищут в самой CRM. У Ozon его нет. */
  number: number | null;
  status: string | null;
  date: string | null;
  date_fact: string | null;
  warehouse: string | null;
  region: string | null;
};

export type BarcodePlace = {
  place_id: number;
  name: string | null;
  /** Номер короба на складе. У мест, коробом не являющихся, смысла не несёт. */
  code: string;
  /** Код места: им вещь СКАНИРУЮТ, когда кладут и достают. */
  place_barcode: string | null;
  korob_barcode: string | null;
  group_id: number | null;
  group: string | null;
  status: string | null;
  city_id: number | null;
  city: string | null;
  /**
   * Место города в справочнике CRM (`sPlace_locationID.sortOrder`) — им
   * выстроены кнопки отбора. Порядок заведён в CRM и один на все экраны, а не
   * посчитан у нас, поэтому кнопки стоят на одном месте от товара к товару.
   */
  city_order: number | null;
  korob: boolean;
  /** Поставка, в которую этот короб отдан, или `null` — его ещё набирают. */
  supply: PlaceSupply | null;
  units: number;
  /** Когда сюда положили последнюю из лежащих здесь вещей. */
  placed: string | null;
  moves_in: number;
  moves_out: number;
  last_in: string | null;
  last_out: string | null;
};

/**
 * Один баркод: что это за вещь, где она лежит и где лежала.
 *
 * `serials` считает ВСЕ вещи этого размера, а не только годные: у баркода
 * 2048334281588 их 51, и 42 из них списаны. Без этих чисел девять строк
 * остатка читались бы как «всего девять вещей».
 */
export type Barcode = {
  barcode: string;
  card: SerialCard;
  serials: { total: number; live: number; gone: number };
  units: number;
  /** Где лежит сейчас — по убыванию числа вещей. */
  places: BarcodePlace[];
  /** Где лежал — по дате последнего изъятия, свежее сверху. */
  past: BarcodePlace[];
};

/**
 * Что контроль FBS нашёл про ОДИН размер.
 *
 * Правила те же и единственные — реестр `repo/fbs_control.py::CHECKS`, поэтому
 * и форма находки здесь общая с разделом «FBS Контроль» (`FbsCheck`): экран
 * рисует их одним компонентом, а разойдись два ответа на один вопрос — карточка
 * баркода успокаивала бы там, где раздел контроля показывает ошибку.
 *
 * `serials` — сколько вещей этого размера участвовало в сверке, ВСЕ, включая
 * списанные: контроль говорит как раз про них.
 */
export type BarcodeControl = {
  barcode: string;
  card_id: number;
  serials: number;
  scanned: {
    tasks: number;
    with_serial: number;
    moves: number;
    moves_missing: number;
  };
  checks: FbsCheck[];
  found: number;
};

/** Место, где вещь лежит сейчас: полка, короб, зал, «Списано». */
export type SerialPlace = {
  place_id: number;
  name: string | null;
  /** Код места: им вещь СКАНИРУЮТ, когда кладут и достают. */
  barcode: string | null;
  /** Собственный штрихкод короба. Пуст у всего, что коробом не является. */
  korob_barcode: string | null;
  group_id: number | null;
  group: string | null;
  status: string | null;
  city_id: number | null;
  city: string | null;
  city_short: string | null;
};

/** Переезд, которым вещь попала туда, где лежит (`sSerial.moveID`). */
export type SerialArrival = {
  move_id: number;
  at: string;
  type_id: number;
  type: string | null;
  user: string | null;
  from: MovePoint;
};

/**
 * Строка списания, на которую указывает серия.
 *
 * `available` здесь — что СДЕЛАЛИ этой строкой (0 списали, 1 вернули в
 * оборот), а не состояние вещи. Состояние говорит `Serial.available`.
 */
export type SerialSpisanie = {
  spisanie_id: number;
  available: number;
  category_id: number;
  category: string | null;
  comment: string | null;
  user: string | null;
  at: string;
};

/** Товар серии: та же карточка маркетплейса, что и в разделе «Товары». */
export type SerialCard = {
  card_id: number;
  model_id: number;
  barcode: string;
  size: string;
  color: string;
  article: string;
  ozon_sku: number;
  name: string;
  image_url: string;
};

/**
 * Одна физическая вещь: где лежит, как попала, списана ли и что это за товар.
 *
 * `available` — состояние вещи и единственный ответ на «списана ли она»:
 * 1 доступна, 0 списана. `size`/`color` — СОБСТВЕННЫЕ поля серии, они
 * остаются единственным описанием, когда карточки маркетплейса нет (`card`
 * тогда `null`).
 */
export type Serial = {
  serial: number;
  article: number;
  available: number;
  deleted: number;
  date_arrive: string | null;
  size: string | null;
  color: string | null;
  /** Когда вещь положили на текущее место (`sSerial.placeDate`). */
  place_date: string | null;
  place: SerialPlace | null;
  arrived: SerialArrival | null;
  spisanie: SerialSpisanie | null;
  card: SerialCard | null;
};

/**
 * Ответ двери `POST /api/serial/{serial}/card` — пересортицы.
 *
 * `already` — вещь УЖЕ числилась этой карточкой, и записи не было: дверь
 * принимает целевое состояние, поэтому повтор безобиден и отчитаться о работе
 * дважды экран не должен.
 *
 * `changed` — поля серии, которые правка задела, именами колонок CRM. Их
 * называет сервис, а не считает экран: размер вещи пересчитывается по
 * справочнику `sMatch` («48» -> «46-48-50»), и повторить это правило на
 * клиенте значило бы завести ему второе определение.
 */
export type SerialRecard = {
  ok: boolean;
  user_id: number;
  serial: number;
  card_id: number;
  from_card_id: number | null;
  model_id: number | null;
  /** Карточка была ЧУЖОЙ модели: сменился товар, описание вещи обнулено. */
  model_changed: boolean;
  already: boolean;
  changed: Record<string, string | number | null>;
};

/**
 * Остаток одного баркода на складе Wildberries.
 *
 * Три числа, и складывать их нельзя: `amount` — то, что покупатель видит на
 * полке; `to_client` уже уехало покупателю и не вернётся; `from_client` едет
 * обратно возвратом и вернётся, но не завтра. Пусто на полке — это `amount`
 * равный нулю, и только он (решение владельца 08.09.26).
 */
export type BoxWbStock = {
  amount: number;
  to_client: number;
  from_client: number;
};

/** Одна позиция МЕСТА: баркод, его размер и сколько штук здесь лежит. */
export type PlaceLine = {
  card_id: number;
  model_id: number;
  barcode: string;
  name: string;
  size: string;
  /**
   * Фотография товара маркетплейса.
   *
   * На экране она стоит ВМЕСТО имени (задача владельца 10.09.26): разбирающий
   * короб держит вещь в руках, и картинка отвечает на «эта ли» быстрее, чем
   * строка «Платье вечернее праздничное сетка», стоящая у пяти позиций подряд.
   */
  image_url: string;
  /** Когда сюда положили последнюю вещь этой позиции. */
  placed: string | null;
  units: number;
  /** `null` — товар на ВБ не заведён вовсе. Это не то же, что нулевой остаток. */
  wb: BoxWbStock | null;
  missing: boolean;
};

/**
 * Позиция, которая через МЕСТО прошла и здесь больше не лежит.
 *
 * Соседка `PlaceLine` и намеренно не она: полки ВБ у такой позиции нет вовсе —
 * вернуть на витрину поставкой она ничего не может, и приехав нулём рядом с
 * составом, задрала бы «вернёт на полку» вдвое.
 *
 * `last_out` — когда её забрали отсюда в последний раз; по нему список и
 * отсортирован (задача владельца 10.09.26).
 */
export type PlacePast = {
  card_id: number;
  model_id: number;
  barcode: string;
  name: string;
  size: string;
  image_url: string;
  moves_in: number;
  moves_out: number;
  last_in: string | null;
  last_out: string | null;
};

/**
 * Короб в списке.
 *
 * `missing` — СРОЧНОСТЬ: сколько позиций короб вернёт на полку ВБ. `null`
 * означает, что зеркало кабинета недоступно и срочность не посчитана; ноль
 * читался бы как «дефицита нет», и это разные ответы.
 */
export type BoxRow = {
  place_id: number;
  /** Номер короба — то, чем его зовут на складе. */
  code: string;
  /** Код места: им короб СКАНИРУЮТ. */
  place_barcode: string;
  korob_barcode: string;
  city_id: number;
  city: string;
  barcodes: number;
  units: number;
  missing: number | null;
  missing_units: number | null;
  unknown: number | null;
};

export type BoxCity = {
  city_id: number;
  name: string;
  boxes: number;
  units: number;
  missing: number | null;
};

/** Час снимка чужой полки: совет стоит ровно столько, сколько его свежесть. */
export type BoxWbState = { available: boolean; synced_at: string };

export type Boxes = {
  filter: { city_id: number; limit: number };
  wb: BoxWbState;
  total: number;
  shown: number;
  limit: number;
  totals: {
    boxes: number;
    units: number;
    barcodes: number;
    missing: number | null;
    urgent_boxes: number | null;
  };
  cities: BoxCity[];
  rows: BoxRow[];
};

/**
 * Подсорт: одна позиция, которой не хватает витрине выбранной площадки.
 *
 * Объект здесь — БАРКОД, то есть один размер: им товар лежит на полке, им же
 * его возьмёт сборщик. Соседний раздел «Короба» отвечает на «какой короб
 * везти первым», этот — на «какого товара довезти и сколько».
 */
export type PodsortRow = {
  barcode: string;
  card: SerialCard | null;
  /** Штук у нас в коробах — по выбранному городу, если он выбран. */
  units: number;
  /** В скольких коробах лежит: одна штука из семи коробов — это работа. */
  boxes: number;
  /** Штук ждёт сборка FBS (любая площадка): вещь ещё в коробе, но её уже продали. */
  reserved: number;
  /** Штук в открытых задачах подсорта ЛЮБОЙ площадки: их возьмут из тех же коробов. */
  claimed: number;
  /** В работе у ЭТОЙ площадки: её открытые задачи и её короба подсорта. */
  planned: number;
  /** Сколько можно тронуть: остаток минус бронь и задачи. */
  free: number;
  /**
   * Заказов за неделю по ТОВАРУ маркетплейса целиком, а не по этому размеру.
   * `null` — дневной срез кабинета не приехал; это не «не заказывали».
   */
  orders: number | null;
  /** Номер ТОВАРА маркетплейса. `0` — у карточки он не проставлен. */
  product_id: number;
  /** СКОЛЬКО СОБРАТЬ — и ровно столько уедет в задачи по «Создать». */
  need: number;
  by_city: PodsortRowCity[];
  /** Полка площадки тремя числами. `null` — товар на площадке не заведён. */
  shelf: BoxWbStock | null;
};

/** Остаток ОДНОЙ позиции в одном городе: где именно её брать. */
export type PodsortRowCity = {
  city_id: number;
  city: string;
  city_order: number;
  units: number;
  boxes: number;
};

/**
 * Кнопка города: сколько подсорта найдётся, если сузить этим городом.
 *
 * Сумма по кнопкам БОЛЬШЕ итога, и это не ошибка счёта: позиция, лежащая и в
 * Воронеже, и в Ростове, доступна обоим сборщикам и стоит на обеих кнопках.
 */
export type PodsortCity = {
  city_id: number;
  name: string;
  lines: number;
  units: number;
  boxes: number;
};

export type Podsort = {
  filter: {
    /** `wb` | `ozon` — ровно одна площадка. */
    platform: string;
    city_id: number;
    /** `any` | `yes` | `no` — по каждому из трёх чисел полки. */
    shelf: string;
    to_client: string;
    from_client: string;
    /** Норма подсорта: сколько штук везём на позицию. */
    norm: number;
    /** Сколько штук обязано остаться в коробах ПОСЛЕ подсорта. */
    keep: number;
    limit: number;
  };
  /** Час снимка чужой полки: совет стоит ровно столько, сколько его свежесть. */
  shelf: { platform: string; available: boolean; synced_at: string };
  /** Бронь FBS: учтена или нет. */
  reserve: { known: boolean; status: string; units: number };
  /** Задачи подсорта: учтены ли, сколько штук открыто у всех и у выбранной. */
  tasks: { known: boolean; units_all: number; units_mine: number; in_baskets: number };
  /**
   * Свежесть статистики заказов. `last_day = ""` — среза нет вовсе. `scope` —
   * чем считаны заказы: `product` (ВБ, товар целиком) или `sku` (Ozon, размер).
   */
  stat: { days: number; last_day: string; scope: "product" | "sku" };
  total: number;
  shown: number;
  limit: number;
  totals: {
    lines: number;
    units: number;
    /** Главное число раздела: сколько штук поедет при этой норме. */
    need: number;
    reserved: number;
    /** Позиций придержано: всё в брони либо это последние штуки. */
    held: number;
    held_units: number;
    /** Позиций уже в работе: норму закрывают задачи и короба подсорта. */
    covered: number;
    covered_units: number;
    /** Объём проверки: сколько позиций склада просмотрено. */
    scanned: number;
    /** Позиций, не заведённых на площадке: подсортом они не оживут. */
    unknown: number;
  };
  cities: PodsortCity[];
  rows: PodsortRow[];
};

/** Ответ «Создать»: сколько задач поставлено и под какими номерами. */
export type PodsortCreated = {
  platform: string;
  city_id: number;
  positions: number;
  created: number;
  task_ids: number[];
  /** Подпись автора записана. `false` — задачи есть, подписи нет (журнал сервиса). */
  signed: boolean;
};

/** Одна задача подсорта из базы соседа `fbs`. Время — московское. */
export type PodsortTask = {
  task_id: number;
  platform: string;
  city: { city_id: number; name: string };
  status: { code: string; name: string };
  card: SerialCard | null;
  card_id: number;
  created_at: string | null;
  claimed_at: string | null;
  done_at: string | null;
  cancelled_at: string | null;
  /** Кто поставил. `null` — задача генератора соседа, а не наша. */
  author: { user_id: number; name: string } | null;
  /** Кто взял в работу — сборщик FBS. */
  user: { user_id: number; name: string } | null;
  serial: number | null;
  move_id: number | null;
  from_place: { place_id: number; name: string } | null;
  basket: { place_id: number; name: string } | null;
};

export type PodsortTasks = {
  filter: { platform: string; city_id: number; status: string; limit: number };
  total: number;
  shown: number;
  platforms: { platform: string; count: number }[];
  cities: { city_id: number; name: string; count: number }[];
  statuses: { code: string; name: string; count: number }[];
  rows: PodsortTask[];
};

/**
 * Лента движений ОДНОГО МЕСТА: что сюда приходило и что уходило.
 *
 * Третья лента склада и та же форма строки, что у двух первых (`MoveRow`):
 * перемещение — одна сущность, и названное здесь одним набором полей, а в
 * ленте серии другим, оно читалось бы как два разных события.
 *
 * Живёт в разделе «Место» (`GET /api/places/{id}/history`), а не в «Коробах»:
 * вопрос «что здесь происходило» задают любому месту склада, и карточка короба
 * зовёт ту же дверь, что карточка стола подбора.
 */
export type PlaceHistory = {
  place_id: number;
  /** Баркод, которым лента сужена, или `null` — всё место. */
  card_id: number | null;
  count: number;
  has_more: boolean;
  tasks_known: boolean;
  moves: MoveRow[];
};

/**
 * КАРТОЧКА МЕСТА — единственная карточка одной точки склада.
 *
 * Отвечает на три вопроса разом: что это за место, что здесь лежит СЕЙЧАС
 * (`lines`) и что здесь ЛЕЖАЛО (`past`). Лента перемещений — отдельной дверью,
 * у неё свой потолок и своя кнопка «ещё».
 *
 * Место — любое: короб хранения, короб отгрузки FBS, стол подбора, торговый
 * зал, «РНД Списано». Условий на группу нет вовсе, а профильные разделы
 * подсказаны признаками:
 *
 *     korob   короб ПО КАНОНУ — у него спрошена полка ВБ и есть список «Короба»
 *     fbs     `wb`/`ozon` — короб отгрузки, карточка в «FBS Короба»
 *
 * До 11.09.26 у короба была ВТОРАЯ карточка (`/boxes/<место>`), а у стола
 * подбора и зала не было состава вовсе; решением владельца две карточки слиты
 * в эту.
 *
 * Имя и номер едут ПОРОЗНЬ намеренно: имя короба в CRM городу не равно и часто
 * ему противоречит («Невиномыск Невиномыск Короб 10944» стоит в Ростове), и
 * зовёт его в интерфейсе общая `lib/places.ts::placeLabel`.
 */
export type PlaceCard = {
  place_id: number;
  name: string | null;
  /** Номер места (`sPlace.sortOrder`). У коробов — их номер, у прочих 10000. */
  code: string;
  /** Код места: им место СКАНИРУЮТ. */
  place_barcode: string | null;
  korob_barcode: string | null;
  group_id: number;
  group: string | null;
  status: string | null;
  city_id: number | null;
  city: string | null;
  city_order: number | null;
  korob: boolean;
  fbs: string | null;
  /** Разных баркодов здесь сейчас — «сколько позиций». */
  barcodes: number;
  /** Годных вещей здесь сейчас, включая те, у которых карточки нет. */
  units: number;
  /**
   * Из них БЕЗ карточки маркетплейса: место занимают, позицией не являются.
   * Без этого числа место «Свободный» с 11 908 вещами и ни одной позицией
   * выглядело бы пустым (замер 11.09.26).
   */
  units_unknown: number;
  /** Список позиций обрезан потолком: их здесь больше, чем приехало. */
  lines_capped: boolean;
  /**
   * Полка ВБ спрошена — а значит, есть срочность. `null` у места, которое не
   * короб: «вернёт на полку» это вопрос про то, что короб повезёт.
   */
  wb: BoxWbState | null;
  /** Позиций с пустой полкой ВБ. `null` — не спрашивали либо зеркало молчит. */
  missing: number | null;
  missing_units: number | null;
  unknown: number | null;
  lines: PlaceLine[];
  past: PlacePast[];
  /**
   * Что списали ОТСЮДА и почему (задача владельца 11.09.26).
   *
   * Место здесь ИСТОЧНИК: списание уносит вещь в «<ГОРОД> Списано», и причина,
   * показанная на приёмнике, отвечает не на тот вопрос. «Из этого короба
   * списали вещь по категории „Не нашли“» — это про КОРОБ: там её не оказалось.
   */
  spisanie: PlaceSpisanie | null;
};

/** Причина списания и сколько вещей ушло по ней ИЗ места. */
export type PlaceSpisanieCategory = {
  category_id: number;
  /** `null` — категории нет в справочнике CRM; тогда её зовут номером. */
  category: string | null;
  units: number;
  last_at: string | null;
};

/**
 * Списания ИЗ места: итог по причинам и сами операции.
 *
 * `rows` — ТЕ ЖЕ строки, что в разделе «Списание» и на карточке баркода
 * (`SpisanieRow`), и каждая называет серию: списание разбирают от вещи
 * (задача владельца 11.09.26). Страница, а не всё сразу — у места 8196
 * списаний 3 811.
 *
 * `units` считается по ПРИЧИНАМ, а не по числу строк: причины знают всю
 * историю места, включая вещи без карточки маркетплейса, которых у места
 * «Свободный» все девять из девяти (замер 11.09.26).
 */
export type PlaceSpisanie = {
  units: number;
  /** Строк больше страницы — дальше их показывает журнал, сужённый местом. */
  has_more: boolean;
  categories: PlaceSpisanieCategory[];
  rows: SpisanieRow[];
};

/**
 * Ответ поиска короба по номеру либо по коду места.
 *
 * СПИСОК, даже когда короб один: `sortOrder` уникальным не объявлен, и день,
 * когда два живых короба получат один номер, не должен обернуться тихим
 * открытием чужого места.
 */
export type BoxFound = {
  code: string;
  city_id: number;
  count: number;
  rows: BoxRow[];
};

/**
 * Отгрузка, которой уехал короб FBS: акт перевозки Ozon или поставка ВБ.
 *
 * `id` строкой, потому что у платформ он разной породы: 120717529 у Ozon,
 * `WB-GI-274224275` у ВБ. `date` — когда уехал; у открытой поставки её нет
 * вовсе, и подставлять туда дату создания нельзя.
 */
export type FbsSupply = {
  id: string;
  status: string;
  label: string;
  date: string;
  created: string;
  shipped: boolean;
};

/**
 * Короб отгрузки в списке.
 *
 * `shipped` — сколько вещей короба УЖЕ у маркетплейса и, значит, ждут
 * списания; `here` — сколько ещё лежит у нас. `null` у обоих означает, что
 * базу задач соседа прочитать не удалось: ноль читался бы как «всё списано».
 */
export type FbsBoxRow = {
  place_id: number;
  /** Номер короба — то, чем его зовут глазами. */
  code: string;
  name: string;
  /** Код места: им короб СКАНИРУЮТ. */
  place_barcode: string;
  city_id: number;
  city: string;
  units: number;
  shipped: number | null;
  here: number | null;
  supply: FbsSupply | null;
};

export type FbsCity = {
  city_id: number;
  name: string;
  boxes: number;
  units: number;
  shipped: number | null;
};

/**
 * Что удалось прочитать у соседа. Флага два, потому что и источников два:
 * `supply` — база поставок, `units` — база задач сборки. В песочной полосе у
 * ВБ первый доступен, а второй нет.
 */
export type FbsNeighbour = { supply: boolean; units: boolean };

export type FbsBoxes = {
  platform: string;
  platform_name: string;
  group_id: number;
  filter: { city_id: number; limit: number };
  neighbour: FbsNeighbour;
  total: number;
  shown: number;
  limit: number;
  totals: {
    /** Мест в группе ВСЕГО — вместе с разобранными коробами. */
    boxes_total: number;
    boxes: number;
    units: number;
    shipped: number | null;
    here: number | null;
    supplies: number;
  };
  cities: FbsCity[];
  rows: FbsBoxRow[];
};

/**
 * Отмена отправления Ozon: когда, кем и из какого состояния.
 *
 * `after_ship` — вердикт САМОГО КАБИНЕТА, и `null` у него третий ответ, а не
 * «нет»: кабинет промолчал. Наш собственный вердикт «вещь уехала» даёт акт
 * перевозки, и когда два ответа расходятся, экран показывает оба.
 *
 * `before_label` — состояние ДО отмены словами («передаётся в доставку»).
 * Именно оно отвечает на «успели ли сдать посылку».
 */
export type FbsCancel = {
  after_ship: boolean | null;
  reason: string;
  by: string;
  before: string;
  before_label: string;
};

/** Одна вещь в коробе отгрузки: что это и что с ней у маркетплейса. */
export type FbsUnit = {
  serial: number;
  name: string;
  size: string;
  barcode: string;
  model_id: number;
  card_id: number;
  /** Номер отправления Ozon либо задания ВБ — по нему вещь ищут в кабинете. */
  document: string;
  status: string;
  shipped: boolean;
  supply: FbsSupply | null;
  /** Наша складская задача. Ноль — задачи нет, вещь положили в короб руками. */
  task_id: number;
  /** Разбор отмены у Ozon. `null` — отмены не было либо площадка не Ozon. */
  cancel: FbsCancel | null;
};

export type FbsBoxCard = {
  platform: string;
  platform_name: string;
  place_id: number;
  code: string;
  name: string;
  place_barcode: string;
  city_id: number;
  city: string;
  neighbour: FbsNeighbour;
  units: number;
  shipped: number | null;
  here: number | null;
  supply: FbsSupply | null;
  rows: FbsUnit[];
};

/**
 * Одна находка контроля FBS.
 *
 * `serial` и `tasks` — не текст, а АДРЕСА: экран строит по ним ссылки на
 * карточку вещи и карточки задач, чтобы разбор начинался прямо из находки.
 * `lines` уже собраны дверью: смысл находки знает домен, а не интерфейс, и
 * новое правило контроля не должно требовать правки экрана.
 */
export type FbsFinding = {
  serial: number | null;
  tasks: number[];
  lines: string[];
};

/** Один контроль реестра: чем он занят и что нашёл. */
export type FbsCheck = {
  code: string;
  title: string;
  /** Вопрос человеческим языком. Без него «0» не говорит, ЧТО проверено. */
  question: string;
  found: number;
  items: FbsFinding[];
  /** Разобранное: не требует работы, но видно, кто, когда и почему скрыл. */
  hidden: FbsHiddenFinding[];
  /**
   * Только у `serial_reuse`: какие короба пересчитать. `null` — CRM не
   * ответила (это не «коробов нет»), поля нет — у правила пересчёта не бывает.
   */
  recount?: FbsRecount | null;
};

/** Короб к пересчёту — один на все находки, которые на него указали. */
export type FbsRecountBox = {
  place_id: number;
  code: string;
  name: string;
  city: string;
  /** Сколько всего вещей в коробе по учёту — с этим числом сверяться. */
  total: number;
  barcodes: { barcode: string; units: number }[];
  /** Серии-находки, из-за которых короб в списке. */
  serials: number[];
};

export type FbsRecount = {
  boxes: FbsRecountBox[];
  items: {
    serial: number;
    card: SerialCard | null;
    /** Салоны, где шёл подбор: короба ищутся только там. */
    cities: string[];
    boxes: number[];
  }[];
};

/** Находка, отмеченная «разобрано». `at` — московское время отметки. */
export type FbsHiddenFinding = FbsFinding & {
  note: string;
  by: string;
  at: string;
};

/**
 * Ответ раздела «FBS Контроль».
 *
 * `scanned` стоит рядом с находками намеренно: «ошибок нет» без объёма
 * проверки неотличимо от «данных не было».
 *
 * Число находок ОДНО: законные случаи (отменённая задача рядом с живой) в
 * ответ не попадают вовсе, поэтому делить находки на «настоящие» и остальные
 * больше не на что.
 */
export type FbsControl = {
  scanned: {
    tasks: number;
    with_serial: number;
    moves: number;
    moves_missing: number;
    /** Вещей на столах подбора — объём правила «зависла на столе». */
    on_pick: number;
  };
  checks: FbsCheck[];
  found: number;
};

/**
 * Одна операция списания: что списали, откуда и когда.
 *
 * `available` — СТОРОНА операции, а не состояние вещи: 0 списали, 1 вернули в
 * оборот. Обе стоят в одном списке, потому что это строки одной таблицы CRM.
 *
 * `place` — откуда вещь ушла, и берётся оно у ПЕРЕМЕЩЕНИЯ списания, а не у
 * серии: списание уводит вещь в «<ГОРОД> Списано», поэтому её нынешнее место
 * ответило бы одним и тем же адресом у всех строк подряд. `null` значит «у
 * строки нет перемещения» — у старых записей его нет вовсе.
 *
 * Форма места — та же `MovePoint`, что у концов ленты движений: «откуда
 * списали» и «откуда переехало» — один и тот же ответ склада.
 */
export type SpisanieRow = {
  /** `null` — только у излишка FBS в детализации сводки: строки списания у
   * него нет, строку адресует `move_id`. */
  spisanie_id: number | null;
  move_id?: number | null;
  serial: number;
  available: number;
  at: string;
  comment: string | null;
  category_id: number;
  category: string | null;
  user: string | null;
  place: MovePoint | null;
  /** Товар той вещи, что списали. `null` — её не заводили на маркетплейс. */
  card: SerialCard | null;
};

/**
 * Причина списания в отборе: чем зовётся, сколько строк, когда последняя.
 *
 * `rows` меряет ТОТ ЖЕ набор, что покажет список: в журнале это весь склад, на
 * карточке баркода — только его вещи.
 */
export type SpisanieCategory = {
  category_id: number;
  name: string;
  active: boolean;
  rows: number;
  last: string | null;
};

/**
 * Журнал списаний, свежие сверху.
 *
 * `category_id` едет НАЗАД намеренно: ответ обязан называть, на какой вопрос
 * отвечает, — четыре строки «Не нашли» иначе неотличимы от четырёх последних
 * списаний склада целиком. Ноль означает «все причины».
 */
export type SpisanieJournal = {
  category_id: number;
  /**
   * Место, которым сужен журнал, или `null`.
   *
   * Сюда приходят с карточки места, когда её полсотни строк кончились: ответ
   * обязан говорить, на какой вопрос отвечает, — иначе строки одного короба
   * неотличимы от последних списаний склада.
   */
  place_id: number | null;
  count: number;
  has_more: boolean;
  limit: number;
  categories: SpisanieCategory[];
  rows: SpisanieRow[];
};

/**
 * Списания ОДНОГО размера — тот же журнал, суженный вещами этого баркода.
 *
 * `serials` — сколько вещей размера участвовало в отборе: пустой список у
 * товара из пятидесяти одной вещи и у товара из одной читаются по-разному.
 */
/** Строка разбивки города: `out` — списано, `in` — оприходовано.
 * `source: "fbs"` — излишек инвентаризации FBS, у него причины нет;
 * `category_id: null` у `spisanie` — строка списания без причины. */
export type SummaryLine = {
  side: "out" | "in";
  category_id: number | null;
  name: string;
  qty: number;
  source: "spisanie" | "fbs";
};

export type SummaryCity = {
  /** `null` — у операции нет места, город неизвестен. */
  city_id: number | null;
  city: string;
  spisano: number;
  oprihod: number;
  lines: SummaryLine[];
};

/** `GET /api/spisanie/summary` — оприходование и списание по городам за
 * период, без потока маркетплейса (`excluded`). Период едет назад: при пустом
 * адресе его считает сервис. */
export type SpisanieSummary = {
  from: string;
  to: string;
  days: number;
  spisano: number;
  oprihod: number;
  excluded: { category_id: number; name: string }[];
  cities: SummaryCity[];
};

/** `GET /api/spisanie/summary/lines` — какие вещи стоят за цифрой сводки.
 * Строки — формой журнала: их рисует тот же `SpisanieFeed`. */
export type SpisanieSummaryLines = {
  from: string;
  to: string;
  side: "out" | "in";
  limit: number;
  has_more: boolean;
  rows: SpisanieRow[];
};

export type BarcodeSpisanie = SpisanieJournal & {
  barcode: string;
  card_id: number;
  serials: number;
};

/**
 * Справочник причин для ОКНА списания (`GET /api/spisanie/categories`).
 *
 * Причину там выбирают, а справочник `sSerial_spisanie_category` расширяемый:
 * зашитый в экран список разошёлся бы с CRM на первой же новой строке.
 * `default_id` тоже приезжает из сервиса — умолчание окна и умолчание двери
 * это одно решение, и жить оно обязано в одном месте.
 */
export type SpisanieCategories = {
  default_id: number;
  categories: SpisanieCategory[];
};

/** Итог по ОДНОЙ серии внутри ответа списания. */
export type SpisanieResult = {
  ok: boolean;
  serial: number;
  /** Целевое состояние вещи: 0 списана, 1 в обороте. */
  available?: number;
  spisanie_id?: number;
  move_id?: number;
  place_id?: number;
  already?: boolean;
  error?: string;
  detail?: string;
};

/**
 * Ответ двери `POST /api/spisanie` — кнопки «Списать» на карточке СЕРИИ.
 *
 * Дверь пакетная и отвечает 200 всегда: серии в пакете независимы, и отказ по
 * одной не отменяет состоявшиеся списания остальных. Поэтому «получилось ли»
 * читается НЕ по коду ответа, а по строке своей серии в `results` — карточка
 * шлёт пакет из одной вещи, и строка у неё ровно одна.
 *
 * `already` — состояние уже было таким, и второй записи в CRM не появилось.
 * Это не отказ и не работа: повтор по номеру серии безобиден по построению,
 * и ключа однократности у этой двери поэтому нет вовсе — в отличие от двери
 * по баркоду, где «какую именно вещь» выбирает склад.
 */
export type SpisanieEdit = {
  user_id: number;
  done: number;
  already: number;
  failed: number;
  results: SpisanieResult[];
};

/**
 * Ответ двери `POST /api/spisanie/barcode` — кнопки «Списать» на карточке места.
 *
 * `left` — сколько годных вещей этого баркода в месте ОСТАНЕТСЯ. Считает его
 * дверь по тому же отбору, которым выбраны серии: пересчитай экран сам, он
 * назвал бы число, к этому ответу не относящееся.
 *
 * `repeat` — повтор по ключу однократности: ответ ТОТ ЖЕ, но списания второй
 * раз не было. Различать обязательно, иначе экран отчитается о работе дважды.
 */
export type SpisanieByBarcode = {
  request_id: string;
  user: { id: number; name: string; short: string };
  barcode: string;
  place_id: number;
  category_id: number;
  requested: number;
  ok: boolean;
  done: number;
  failed: number;
  left: number;
  repeat?: boolean;
  /** Та же форма товара, что у карточек серии и складской задачи. */
  card: SerialCard | null;
  place: {
    place_id: number;
    place: string | null;
    code: string;
    city: string | null;
    korob: boolean;
    units: number;
  } | null;
  results: SpisanieResult[];
};

/**
 * КОРЕНЬ КЛЮЧЕЙ ЗАПРОСОВ ДАННЫХ — им перечитывают экран после записи, и вызов
 * `invalidateQueries` БЕЗ КЛЮЧА вместо него СТОИЛ РАЗБОРА 16.09.26.
 *
 * Refine раскладывает свои запросы по корням: `["data", …]` — то, что экран
 * прочитал у нас, `["auth"]` — проверка сессии, на которой стоит `<Authenticated>`
 * в `App.tsx`. Инвалидация БЕЗ фильтра накрывает оба, и пока проверка сессии
 * идёт заново, `<Authenticated>` рендерит свой fallback — то есть РАЗМОНТИРУЕТ
 * весь кабинет вместе с открытым окном.
 *
 * Для окна списания это значило вот что: списание проходило, а окно с итогом
 * исчезало вместе с номером строки учёта — единственным, чем человек может
 * проверить необратимую операцию потом. Поймано живым прогоном в песочной
 * полосе: окно держалось ~800 мс до ответа двери и пропадало ровно на нём,
 * `onClose` при этом не звался ни разу.
 */
export const DATA_QUERIES = ["data"] as const;

/**
 * dataProvider под наш API.
 *
 * Отчёт ничего не меняет — он читает и советует. Поэтому реализован ровно
 * один метод, которым экран и ходит; остальные честно отказывают. Заглушки
 * «на будущее» — это код, который никто не проверял, но все считают рабочим.
 */
export const dataProvider: DataProvider = {
  getApiUrl: () => "/api",

  custom: async ({ url, method }) => {
    if (method !== "get") throw new Error(`Метод ${method} не поддерживается`);
    return { data: (await api.get<unknown>(url)) as never };
  },

  getList: async () => {
    throw new Error("Списочных ресурсов нет: экран ходит за отчётом целиком");
  },
  getOne: async () => {
    throw new Error("Списочных ресурсов нет: экран ходит за отчётом целиком");
  },
  create: async () => {
    throw new Error("Отчёт ничего не создаёт");
  },
  update: async () => {
    throw new Error("Отчёт ничего не меняет");
  },
  deleteOne: async () => {
    throw new Error("Отчёт ничего не удаляет");
  },
};
